<template>
  <ServicePageLayout
    icon="fas fa-hands-helping"
    title="Student Welfare"
    subtitle="Supporting student success through comprehensive welfare services"
  >
    <!-- Overview -->
    <section class="content-section">
      <h2>
        <i class="fas fa-info-circle section-icon" />
        Overview
      </h2>
      <p>
        The Student Welfare unit is dedicated to ensuring the well-being and success of every
        student. We provide a range of support services designed to help students navigate their
        academic journey, address personal challenges, and achieve their full potential.
      </p>
    </section>

    <!-- Information & Orientation Services -->
    <section
      id="orientation"
      class="content-section"
    >
      <h2>
        <i class="fas fa-compass section-icon" />
        Information & Orientation Services
      </h2>
      <p>
        We provide essential information and orientation programs to help students successfully
        transition into college life and stay informed throughout their academic journey.
      </p>
      <div class="services-grid">
        <div class="service-card">
          <div class="card-icon green">
            <i class="fas fa-user-graduate" />
          </div>
          <h3>New Student Orientation</h3>
          <p>Comprehensive orientation program for freshmen and transferees.</p>
        </div>
        <div class="service-card">
          <div class="card-icon yellow">
            <i class="fas fa-map-signs" />
          </div>
          <h3>Campus Tours</h3>
          <p>Guided tours to familiarize students with campus facilities.</p>
        </div>
        <div class="service-card">
          <div class="card-icon green">
            <i class="fas fa-bullhorn" />
          </div>
          <h3>Information Dissemination</h3>
          <p>Regular updates on academic policies, events, and opportunities.</p>
        </div>
        <div class="service-card">
          <div class="card-icon yellow">
            <i class="fas fa-id-card" />
          </div>
          <h3>Student ID Processing</h3>
          <p>Issuance and renewal of student identification cards.</p>
        </div>
      </div>
    </section>

    <!-- Guidance & Counseling -->
    <section class="content-section highlight">
      <div class="highlight-content">
        <div class="highlight-icon">
          <i class="fas fa-heart" />
        </div>
        <div class="highlight-text">
          <h3>Guidance & Counseling Services</h3>
          <p>
            Our professional counselors provide confidential support for personal, academic,
            and career-related concerns. We're here to listen, guide, and help you overcome
            challenges.
          </p>
          <router-link
            to="/services/counseling"
            class="highlight-link"
          >
            Learn More About Counseling
            <i class="fas fa-arrow-right" />
          </router-link>
        </div>
      </div>
    </section>

    <!-- Career & Job Placement -->
    <section class="content-section highlight yellow">
      <div class="highlight-content">
        <div class="highlight-icon yellow">
          <i class="fas fa-briefcase" />
        </div>
        <div class="highlight-text">
          <h3>Career & Job Placement</h3>
          <p>
            Preparing you for the workforce through career counseling, resume workshops,
            job fairs, and employment assistance. We connect students with opportunities
            for internships and employment.
          </p>
          <router-link
            to="/services/career"
            class="highlight-link"
          >
            Explore Career Services
            <i class="fas fa-arrow-right" />
          </router-link>
        </div>
      </div>
    </section>

    <!-- Student Handbook -->
    <section
      id="handbook"
      class="content-section"
    >
      <h2>
        <i class="fas fa-book section-icon" />
        Student Handbook Development
      </h2>
      <p>
        The Student Handbook serves as the official guide for student conduct, rights,
        responsibilities, and institutional policies. It is regularly updated to reflect
        current regulations and best practices.
      </p>
      <div class="handbook-info">
        <div class="handbook-item">
          <i class="fas fa-bookmark" />
          <div>
            <strong>Student Rights & Responsibilities</strong>
            <p>Understanding your rights as a student and your obligations to the institution.</p>
          </div>
        </div>
        <div class="handbook-item">
          <i class="fas fa-balance-scale" />
          <div>
            <strong>Code of Conduct</strong>
            <p>Guidelines for appropriate behavior and academic integrity.</p>
          </div>
        </div>
        <div class="handbook-item">
          <i class="fas fa-exclamation-triangle" />
          <div>
            <strong>Disciplinary Procedures</strong>
            <p>Due process and procedures for handling violations.</p>
          </div>
        </div>
        <div class="handbook-item">
          <i class="fas fa-shield-alt" />
          <div>
            <strong>Grievance Mechanism</strong>
            <p>How to file complaints and seek resolution for concerns.</p>
          </div>
        </div>
      </div>
      <button
        class="download-btn"
        @click="downloadHandbook"
      >
        <i class="fas fa-download" />
        Download Student Handbook
      </button>
    </section>

    <!-- Scholarship & Financial Assistance -->
    <section class="content-section">
      <h2>
        <i class="fas fa-hand-holding-usd section-icon" />
        Scholarship & Financial Aid Referral
      </h2>
      <p>
        We assist students in finding and applying for scholarships, grants, and financial
        assistance programs. Our team can guide you through the application process and
        connect you with relevant opportunities.
      </p>
      <div class="services-grid">
        <div class="service-card">
          <div class="card-icon green">
            <i class="fas fa-graduation-cap" />
          </div>
          <h3>Academic Scholarships</h3>
          <p>Merit-based scholarships for outstanding academic performance.</p>
        </div>
        <div class="service-card">
          <div class="card-icon yellow">
            <i class="fas fa-hands" />
          </div>
          <h3>Financial Assistance</h3>
          <p>Need-based assistance for qualifying students.</p>
        </div>
        <div class="service-card">
          <div class="card-icon green">
            <i class="fas fa-award" />
          </div>
          <h3>Government Programs</h3>
          <p>TES, CHED scholarships, and other government grants.</p>
        </div>
      </div>
    </section>

    <!-- Contact -->
    <section class="content-section">
      <h2>
        <i class="fas fa-phone section-icon" />
        Contact Student Welfare
      </h2>
      <div class="contact-info">
        <div class="contact-item">
          <i class="fas fa-map-marker-alt" />
          <span>Room 201, Student Services Building</span>
        </div>
        <div class="contact-item">
          <i class="fas fa-phone" />
          <span>(078) 123-4567 Local 201</span>
        </div>
        <div class="contact-item">
          <i class="fas fa-envelope" />
          <span>welfare@nec.edu.ph</span>
        </div>
        <div class="contact-item">
          <i class="fas fa-clock" />
          <span>Monday - Friday, 8:00 AM - 5:00 PM</span>
        </div>
      </div>
    </section>
  </ServicePageLayout>
</template>

<script>
import ServicePageLayout from '@/components/landing/ServicePageLayout.vue'

export default {
  name: 'StudentWelfareService',

  components: {
    ServicePageLayout
  },

  metaInfo: () => ({
    title: 'Student Welfare'
  }),

  methods: {
    downloadHandbook () {
      this.$bvToast.toast('Student Handbook download coming soon!', {
        title: 'Coming Soon',
        variant: 'info',
        solid: true
      })
    }
  }
}
</script>
